<template>
    <div class="script--page">
        <base-form
            :modalOnSubmit="{
                has: model.hasAssociatedCalls
            }"
            @open-modal="openConfirmationModal( $event )"
            class="script position--relative"
        >
            <base-card class="script__content" :add-margins="false">
                <template #card__title >
                    <div class="script__header display--grid">
                        <base-field
                            class="script__title"
                            :settings="{
                                label: { class: `${generateLabelClass} script__label`, text: $t('global.scriptName') },
                                input: {
                                    component: 'InputBasic',
                                    id: 'scriptName',
                                    name: 'scriptName',
                                    type: 'text',
                                    class: 'field__input--default typography--default color--default',
                                    value: model.name,
                                    placeholder: $t('global.scriptName'),
                                    browserAutocomplete: 'title',
                                    isRequired: false,
                                    isMissing: errors.missing.scriptName,
                                    noListener: true
                                },
                                isEdition,
                                reset,
                                allowFilling: permissions.allowFilling
                            }"
                        />
                        <base-field
                            class="script__state"
                            :settings="{
                                label: { class: `${generateLabelClass} script__label`, text: $t('global.state') },
                                input: {
                                    component: 'States',
                                    name: 'stateId',
                                    value: model.enabled ? 1 : 2,
                                    isMissing: errors.missing.stateId,
                                    dynamicData: {
                                        states: [ 1, 2 ]
                                    }
                                },
                                isEdition,
                                reset,
                                allowFilling: permissions.allowFilling
                            }"
                        />
                        <base-field
                                class="script__type"
                                :style="`z-index: ${ zIndexes.length + 1 }`"
                                :settings="{
                                    label: { class: `${generateLabelClass} script__label`, text: $t('global.type') },
                                    input: {
                                        component: 'InputAutocomplete',
                                        id: 'typeId',
                                        name: 'typeId',
                                        type: 'text',
                                        placeholder: capitalizeFirstLetter( $t('global.type') ),
                                        value: model.type && model.type.id,
                                        targetClass:
                                            'field__input--default typography--default color--default',
                                        browserAutocomplete: 'off',
                                        isRequired: false,
                                        isMissing: errors.missing.typeId,
                                        dynamicData: {
                                            data: [
                                                {
                                                    value: 1,
                                                    label: $t('scriptTypes.chained'),
                                                },
                                               {
                                                    value: 2,
                                                    label: $t('scriptTypes.sequence'),
                                                }
                                            ],
                                            apiValue: ( model.type ) ? { value: model.type.id, label: model.type.name } : {}
                                        },
                                        errorOnSubmit: {
                                            show: errors.api.typeId
                                                ? true
                                                : false,
                                            messages: errors.api.typeId,
                                        },
                                    },
                                    isEdition,
                                    reset,
                                    allowFilling: permissions.allowFilling && !isEdition,
                                }"
                            />
                    </div>
                </template>
                <template #card__body >
                    <div class="script__questions">
                        <transition-group name="script__questions__content--animation" tag="div" class="script__questions__content">
                            <template  v-for="( { id, question, type, nextQuestions, operation }, index ) in script.questions" >
                                <div
                                    v-if="operation !== 'delete'"
                                    class="script__questions__content__group position--relative"
                                    :key="`question[${id}]`"
                                    :style="`z-index: ${zIndexes[index]}`"
                                >
                                    <script-question
                                        class="script__questions__question position--relative display--grid"
                                        v-bind="{
                                            questions: script.questions,
                                            questionId: id,
                                            values: {
                                                question,
                                                type,
                                                nextQuestions: nextQuestions|| {}
                                            },
                                            scriptType: getScriptType,
                                            isMissing: errors.missing[`question[${id}]`],
                                            isEdition,
                                            reset,
                                            allowFilling: permissions.allowFilling,
                                            generateLabelClass
                                        }"
                                        :data-index="index"
                                    />
                                    <div class="script__questions__question__actions position--absolute">
                                        <template v-if="getScriptType && getScriptType !== 1">
                                            <button-with-icon
                                                v-if="index !== 0"
                                                :icon="{
                                                    component: () => import(
                                                        /* webpackChunkName: 'IconMoveUp' */ '@/components/bundles/layout/images/icons/IconMoveUp'
                                                    )
                                                }"
                                                class="script__questions__question__move-up"
                                                :aria-label="$t('accessibility.moveUp')"
                                                :action="{
                                                    execute: true,
                                                    function: () => controllerQuestion({ questionId: id , behaviorType: 'moveUp' })
                                                }"
                                            />
                                            <button-with-icon
                                                v-if="index !== script.questions.length - 1"
                                                :icon="{
                                                    component: () => import(
                                                        /* webpackChunkName: 'IconMoveDown' */ '@/components/bundles/layout/images/icons/IconMoveDown'
                                                    )
                                                }"
                                                class="script__questions__question__move-down"
                                                :aria-label="$t('accessibility.moveDown')"
                                                :action="{
                                                    execute: true,
                                                    function: () => controllerQuestion({ questionId: id , behaviorType: 'moveDown' })
                                                }"
                                            />
                                        </template>
                                        <button-with-icon
                                            v-if="script.questions.length > 1"
                                            :icon="{
                                                component: () => import(
                                                    /* webpackChunkName: 'IconTrash' */ '@/components/bundles/layout/images/icons/IconTrash'
                                                )
                                            }"
                                            class="script__questions__question__delete"
                                            :aria-label="$t('accessibility.delete')"
                                            :action="{
                                                execute: true,
                                                function: () => controllerQuestion({ questionId: id , behaviorType: 'delete' })
                                            }"
                                        />
                                    </div>
                                </div>
                            </template>
                        </transition-group>
                        <div class="script__questions__add">
                            <button-with-icon
                                :icon="{
                                    component: () => import( /* webpackChunkName: 'IconPlus' */ '@/components/bundles/layout/images/icons/IconPlus' )
                                }"
                                class="script__questions__add__button position--absolute"
                                :aria-label="$t('accessibility.addProp', { prop: $t('accessibility.question') })"
                                :action="{
                                    execute: true,
                                    function: () => controllerQuestion({ questionId: getNewQuestionId })
                                }"
                            />
                        </div>
                    </div>
                </template>
            </base-card>
        </base-form>

        <!-- modal de confirmação para forçar o update do guião -->
        <modal
            v-if="modals.confirmForceEdit"
            :form="{
                class: 'cblock__modal'
            }"
            :customSettings="{
                position: 'center',
                button: {
                    label: 'continue',
                    type: 'button',
                    action: () => submitModal({ additionalParams: { forceUpdate: true} })
                }
            }"
            @close="closeModal( 'confirmForceEdit' )"
            :key="modals.confirmForceEdit"
        >
            <template #modal__header >
                <h2 class="cblock__modal__header">{{ $t('global.editConfirmation') }}</h2>
            </template>
            <template #modal__body__confirm >
                <span class="cblock__modal__info display--block capitalize">
                    {{ $t('warning.forceEditScript') + ' ' + $t('global.areYouSureYouWantTo') + $t('global.continue') + ' ?'}}
                </span>
            </template>
            <template #modal__info >
                <base-button
                    class="modal__content__button--negative background--transparent border--grey"
                    @click="closeModal( 'confirmForceEdit' )"
                    v-html="$t('global.cancel')"
                />
            </template>
        </modal>

        <!-- modal de duplicação de guião -->
        <modal
            v-if="modals.duplicateScript"
            id="duplicateScriptModal"
            :form="{
                class: 'cblock__modal',
                settings: {
                    typeOfPreparation: 'duplicateScript'
                }
            }"
            :customSettings="{
                position: 'center',
                color: 'green',
                button: {
                    label: 'duplicate'
                },
                show: {
                    infoSection: false
                }
            }"
            :executeCloseAction="!loading"
            :preventReset="true"
            @close="closeModal( 'duplicateScript' )"
            :key="modals.duplicateScript"
        >
            <template #modal__header >
                <h2 class="cblock__modal__header">{{ $t('global.duplicateScript') }}</h2>
            </template>
            <template #modal__body >
                <base-field
                    class="script__campaigns"
                    :settings="{
                        label: { class: 'capitalize', text: $t('global.campaign') },
                        input: {
                            component: 'InputAutocomplete',
                            name: 'campaign',
                            id: 'campaign',
                            type: 'text',
                            value: '',
                            targetClass: 'scrip__campaigns__input field__input--default typography--default color--default',
                            browserAutocomplete: 'off',
                            isRequired: true,
                            isMissing: errors.missing.campaign,
                            dynamicData: {
                                data: campaigns.drop,
                                ordering: campaigns.loading
                            },
                            noListener: true
                        },
                        allowFilling: true
                    }"
                />
            </template>
            <template #modal__info >
                <base-button
                    class="modal__content__button--negative background--transparent border--grey"
                    @click="closeModal( 'duplicateScript' )"
                    v-html="$t('global.cancel')"
                />
            </template>
        </modal>

        <!-- modal do preview do guião -->
        <modal
            v-if="modals.preview"
            :form="{
                class: 'script__modal--preview'
            }"
            :customSettings="{
                position: 'center',
                color: 'green',
                show: {
                    actions: false,
                    infoSection: false
                }
            }"
            :preventReset="true"
            @close="closeModal( 'preview' )"
            :key="modals.preview"
        >
            <template #modal__header >
                <h2 class="cblock__modal__header">{{ $t('global.previewOfScript') }}</h2>
            </template>
            <template #modal__body >
                <base-script
                class="panel__card"
                v-bind="{
                    script: previewScriptHelpers.script,
                    allowFilling: permissions.allowFilling,
                    isOperateMenu: true
                }"
            />
            </template>
        </modal>

        <widget-options>
            <widget-button
                v-if="$store.getters.can( 'script', 'preview' )"
                class="capitalize"
                :settings="{
                    action: () => openPreviewScript(),
                    label: 'previewScript'
                }"
            />
            <widget-button
                v-if="!$route.meta.isAdd && $store.getters.can( 'script', 'duplicate' )"
                class="capitalize"
                :settings="{
                    action: () => openModal( 'duplicateScript' ),
                    label: 'duplicateScript'
                }"
            />
        </widget-options>
    </div>
</template>

<script>
// packages
import {
    reactive,
    toRefs,
    computed,
    watch
} from '@vue/composition-api';

// utils
import { cloneObject, transformToInt, isAnObject } from '@/utils/';

// models
import { toggleRequiredFields, resetRequiredFields } from '@/models/helpers/toggle-required-fields';

// store
import { mapState, storePayload, useActions } from '@/hooks/use-store';

// requests
import { get } from '@/helpers/requests';

// helpers
import { sortObjectsArray, capitalizeFirstLetter } from '@/helpers/mixinGeneral';

// hooks
import { singleStructure, useData, portalComponents } from '@/hooks/use-data';
import generateZindex from '@/hooks/use-z-index';

// configs
import { scriptInputTypes, campaignScriptTypes } from '@/configs/const';

export default {
    components: {
        ...singleStructure.components,
        ...portalComponents,
        ScriptQuestion: () => import( /* webpackChunkName: 'ScriptQuestion'  */ '@/components/bundles/script/blocks/Question' ),
        ButtonWithIcon: () => import( /* webpackChunkName: 'ButtonWithIcon' */ '@/components/bundles/buttons/BaseButtonWithIcon' ),
        BaseButton: () => import( /* webpackChunkName: 'BaseButton' */ '@/components/bundles/buttons/BaseButton' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' ),
        WidgetButton: () => import( /* webpackChunkName: 'WidgetButton' */ '@/components/bundles/layout/widget/options/types/WidgetButton' ),
        BaseScript: () => import( /* webpackChunkName: 'BaseScript' */ '@/components/bundles/script/Script' )
    },
    props: {
        ...singleStructure.props
    },
    setup ( props, { root: { $route } }) {
        const { actions } = useActions();

        // definição do tipo de input default.
        const defaultType = { type: { id: 1, label: 'string' } };
        const pageConfigs = {
            operation: 'create',
            questions: [ { id: 1, ...defaultType, operation: 'create' } ]
        };

        const state = reactive({
            script: {
                ...cloneObject( pageConfigs )
            },
            initialScript: {},
            lastIndex: {
                question: 1
            }
        });
        actions( 'saveHelper', { script: state.script });

        const modals = reactive({
            confirmForceEdit: false,
            preview: false,
            duplicateScript: false
        });

        // lista de campanhas
        const requestData = reactive({
            campaigns: {
                drop: [],
                loading: false
            }
        });

        // definição dos campos obrigatórios default na adição.
        const { isAdd } = $route.meta;

        /**
         * [computed]
         * Aplicação se o campo é obrigatório na edição ou não.
         *
         * @since 1.0.0
         * @category computed
         * @autor Tiago Martinho
         *
        */
        const applyRequiredOnEdition = computed( () => ({ ...( !isAdd && { isRequiredOnEdit: true }) }) );

        /**
         * [method]
         * Trigger para dar toggle a obrigatoriedade do campo.
         *
         * @param {String} pageId
         * @param {Number} questionId
         * @param {Boolean} isRequired
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const triggerRequired = ({
            questionId = 1,
            isRequired = true,
            isRequiredOnEdit = false
        }) => {
            toggleRequiredFields( 'script', { field: `question[${questionId}]`, isRequired, isRequiredOnEdit });
        };

        if ( isAdd ) {
            resetRequiredFields( 'script' );
            toggleRequiredFields( 'script', { field: 'scriptName', isRequired: true });
            toggleRequiredFields( 'script', { field: 'stateId', isRequired: true });
            toggleRequiredFields( 'script', { field: 'typeId', isRequired: true });
            triggerRequired({ });
        }

        /**
         * [method]
         * Criação da key `label` para a estruturação
         * da próxima pergunta.
         *
         * @param {Number} questionId
         * @return {Object}
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const generateLabel = questionId => ({ label: props.model.questions.find( ({ id }) => id === questionId )?.question });

        /**
         * [method]
         * Estruturação dos dados da próxima pergunta.
         * Caso só haja apenas uma possibilidade de resposta, significa
         * que basta enviar um objecto.
         * Caso haja mais que uma possibilidade de resposta, significa que
         * é necessário criar às várias opções de resposta.
         *
         * @param {Array} possibleAnswers
         * @return {Array/Object}
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const generateNextQuestions = possibleAnswers => {
            if ( possibleAnswers.length === 1 ) {
                return {
                    id: possibleAnswers[0].unlockQuestionId,
                    ...generateLabel( possibleAnswers[0].unlockQuestionId ),
                    idAnswer: possibleAnswers[0].id
                };
            }

            return possibleAnswers.map( ({
                id,
                unlockQuestionId,
                value,
                additionalBehaviour
            }) => ({
                id,
                value,
                operation: 'update',
                additionalBehaviour,
                nextQuestion: { id: unlockQuestionId, ...generateLabel( unlockQuestionId ) }
            }) );
        };

        /**
         * [method]
         * Estruturação dos dados provenientes da API,
         * de forma a serem apresentados visualmente ao
         * utilizador.
         *
         * @param {Object} newModel
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const structureData = newModel => {
            if ( !Object.keys( newModel ).length )
                return;

            resetRequiredFields( 'script' );
            toggleRequiredFields( 'script', { field: 'scriptName', isRequired: true });
            toggleRequiredFields( 'script', { field: 'stateId', isRequired: true });

            state.script = {
                operation: 'maintain',
                ...( newModel?.type?.id && { typeId: newModel.type.id }),
                questions: newModel.questions
                    .sort( ( questionPrevious, questionNext ) => ( questionPrevious.sequence - questionNext.sequence ) )
                    .map( ({
                        id, question, inputType, possibleAnswers
                    }) => {
                        // definição da questão como campo obrigatório.
                        triggerRequired({ questionId: id });

                        if ( state.lastIndex.question < id )
                            state.lastIndex.question = id;

                        return {
                            id,
                            question,
                            type: { id: inputType?.id || 100, label: inputType?.name || 'noReply' },
                            operation: 'update',
                            nextQuestions: generateNextQuestions( possibleAnswers )
                        };
                    })
            };

            state.initialScript = cloneObject( state.script );

            actions( 'saveHelper', { script: state.script });
        };

        useData( props, structureData );

        const store = reactive({
            payload: mapState({ module: 'storePayload', key: 'payload' }),
            loading: mapState({ module: 'storePayload', key: 'loading' })
        });

        /**
         * [computed]
         * Obter tipo de guião.
         *
         * @since 1.2.3
         * @category computed
         * @autor Jorge Monteiro
         *
        */
        const getScriptType = computed( () => props?.model?.type?.id || transformToInt( store.payload.typeId ) || undefined );

        /**
         * [method]
         * Função de limpa os campos 'próxima pergunta' quando a
         * pergunta à qual este está associado é eliminada.
         *
         * @param {String} payloadKey
         * @param {Object} question
         * @param {Number} deletedQuestionID
         * @since 1.2.2
         * @category method
         * @autor Jorge Monteiro
         *
        */
        const clearNextQuestionField = ( payloadKey, question, deletedQuestionID ) => {
            const nextQuestionPosition = transformToInt( store.payload[payloadKey] );
            const nextQuestionId = ( nextQuestionPosition )
                ? state.script.questions[nextQuestionPosition]?.id
                : question?.id;

            if ( nextQuestionId && nextQuestionId === deletedQuestionID ) {
                actions( 'registerField', { name: payloadKey, value: '' });
                const input = document.getElementById( payloadKey );
                input.value = null;
                // trigger do evento `input`.
                const event = new Event( 'input' );
                input.dispatchEvent( event );
            }
        };

        /**
         * [method]
         * Procedimento de adição & remoção de uma questão ao script.
         * No final é dado o trigger do toggle de obrigatoriedade da questão e
         * guardado na store a estrutura no script.
         *
         * @param {Number} id
         * @param {String} behaviorType
         * @since 1.0.0
         * @category method
         * @autor Tiago Martinho
         *
        */
        const controllerQuestion = ({ questionId, behaviorType = 'add' }) => {
            const behavior = {
                add: () => {
                    // adiciona a nova questão.
                    state.script.questions.push({
                        id: questionId,
                        ...defaultType,
                        operation: 'create'
                    });
                },
                delete: () => {
                    // removido a questão do passo.
                    if ( isAdd ) {
                        // obtenção das próximas perguntas.
                        // de forma a actualizar o valor caso seja preciso.
                        const nextQuestions = Object.keys( store.payload ).filter( key => key.includes( 'next-question' ) );
                        nextQuestions.forEach( nextQuestion => {
                            // transformação para inteiro.
                            const nextQuestionIdInt = transformToInt( store.payload[nextQuestion] );

                            // se o valor da próxima questão, for igual ao ID ou ao index no array
                            // da questão eliminada, é necessário remover estas keys da store.
                            if ( [ questionId, ( questionId - 1 ) ].includes( nextQuestionIdInt ) ) {
                                actions( 'deleteField', { name: nextQuestion });
                                const input = document.getElementById( nextQuestion );
                                input.value = null;

                                // trigger do evento `input`.
                                const event = new Event( 'input' );
                                input.dispatchEvent( event );
                            } else if ( nextQuestionIdInt > questionId )
                            // caso o valor da próxima questão seja superior ao valor da questão apagada
                            // é necessário reduzir o valor, de forma a manter a sequência.
                            // exemplo:
                            // - ( próximas questões ) - 1, 2, 3, 4
                            // - é apagado o 2, o 3 & 4 terão que reduzir uma casa.
                                actions( 'registerField', { name: nextQuestion, value: nextQuestionIdInt - 1 });
                        });

                        state.script.questions = state.script.questions.filter( ({ id }) => id !== questionId );
                    } else {
                        // Obtém a pergunta que se está a tentar eliminar
                        const questionOperation = state.script.questions.find( ({ id }) => id === questionId ).operation;

                        // Se for uma pergunta que já tenha sido criada anteriormente, é alterada a key da
                        // operação de 'update' para 'delete' e adicionado ao payload o id da pergunta a eliminar.
                        // Senão se a pergunta tiver sido criada durante a edição do guião
                        // É eliminada do array das questões do script visto que a API não precisa de
                        // saber que a questão vai ser eliminada, pois ainda não existe na base de dados.
                        if ( questionOperation !== 'create' ) {
                            state.script.questions = state.script.questions.map( question => {
                                if ( question.id === questionId )
                                    question.operation = 'delete';

                                return question;
                            });

                            // se for uma edição
                            // é necessário registar que a questão foi removida.
                            actions( 'registerField', { name: `delete-question[${questionId}]`, value: questionId });
                        } else
                            state.script.questions = state.script.questions.filter( ({ id }) => id !== questionId );

                        // Atualização das opções a aparecer na drop do campo da 'próxima pergunta'.
                        actions( 'saveHelper', { scriptMapedQuestions: getAllNextQuestions( state.script.questions ) });

                        // Remoção do valor dos campos 'próxima pergunta' quando esse campo
                        // estiver a apontar para uma pergunta que foi eliminada
                        state.script.questions.forEach( question => {
                            const inputType = transformToInt( store.payload[`type[${question.id}]`] ) || question.type.id;

                            if ( ![ 3, 4, 5 ].includes( inputType ) )
                                clearNextQuestionField( `next-question[${question.id}]`, question?.nextQuestions, questionId );
                            else {
                                question.nextQuestions.forEach( answer => {
                                    clearNextQuestionField( `next-question[${question.id}]-answer[${answer.id}]`, answer?.nextQuestion, questionId );
                                });
                            }
                        });
                    }
                },
                moveDown: () => {
                    const fromIndex = state.script.questions.findIndex( question => question.id === questionId );
                    const toIndex = fromIndex + 1;

                    const questionToMove = state.script.questions.splice( fromIndex, 1 )[0];
                    state.script.questions.splice( toIndex, 0, questionToMove );

                    // Variável 'hasSorting' é inserida no paylaod de forma a transmitir que algo na
                    // ordenação das perguntas foi alterado e o script precisa de ser gurdado.
                    actions( 'registerField', { name: 'hasSorting', value: true });
                },
                moveUp: () => {
                    const fromIndex = state.script.questions.findIndex( question => question.id === questionId );
                    const toIndex = fromIndex - 1;

                    const questionToMove = state.script.questions.splice( fromIndex, 1 )[0];
                    state.script.questions.splice( toIndex, 0, questionToMove );

                    // Variável 'hasSorting' é inserida no paylaod de forma a transmitir que algo na
                    // ordenação das perguntas foi alterado e o script precisa de ser gurdado.
                    actions( 'registerField', { name: 'hasSorting', value: true });
                }
            };

            // execução do tipo de acção.
            behavior[behaviorType]();

            // Trigger da obrigatoriedade dos novos campos do guião.
            // Conforme o tipo de comportamento, o campo é obrigatório ou não.
            // Não faz o trigger se ação for a ordenação de perguntas.
            if ( ![ 'moveUp', 'moveDown' ].includes( behaviorType ) ) {
                const isRequired = behaviorType === 'add';
                triggerRequired({
                    questionId,
                    ...( isRequired
                        ? { ...applyRequiredOnEdition.value }
                        : { isRequired }
                    )
                });
            }

            // armazenado a estrutura das questões nos helpers.
            actions( 'saveHelper', { script: state.script });
        };

        // Função que retorna questões que poderão ser selecionadas nos campos da 'próxima pergunta'
        const getAllNextQuestions = questions => {
            const mappedQuestions = {};
            if ( questions ) {
                questions.forEach( element => {
                    mappedQuestions[`question[${element.id}]`] = element.question || store.payload[`question[${element.id}]`];
                });
            }
            return mappedQuestions;
        };
        actions( 'saveHelper', { scriptMapedQuestions: getAllNextQuestions( state.script.questions ) });

        /**
         * [computed]
         * Obtenção do novo Id da questão.
         * O novo id da questão vai ser o valor
         * da última posição do array de questões mais
         * 1.
         *
         * @since 1.0.0
         * @category computed
         * @autor Tiago Martinho
         *
        */
        const getNewQuestionId = computed( () => {
            const { questions } = state.script;
            // Se o guião não tiver perguntas, o próximo/ novo id é 1.
            let questionId = 0;

            if ( questions.length ) {
                // Verificação de qual index é que deve ser o gerador do novo index.
                // Dado que a ordenação das questões é relativa a sequence e não ao ID,
                // como tal, vai haver vezes em que o id que esta na última posição da estrutura
                // não deve ser considerado o último id. Como tal, é necessário ir buscar o último id
                // proveniente da API.
                const lastVisualIndex = questions[questions.length - 1].id;
                const lastAPIIndex = state.lastIndex.question || 0;
                questionId = ( lastAPIIndex < lastVisualIndex ) ? lastVisualIndex : lastAPIIndex;
            }

            return questionId + 1;
        });

        const { errors, reset } = storePayload();

        // Aquando o cancelamento da edição por parte do utilizador
        // é necessário gerar a estrutura inicial do script
        watch( () => reset.value,
            newReset => {
                if ( newReset ) {
                    state.script = { ...state.initialScript };
                    actions( 'saveHelper', { script: state.script });
                }
            });

        // obtenção dos z-indexes
        const zIndexes = generateZindex( state.script );

        const generateLabelClass = computed( () => ( $route.meta.isAdd ? 'field__label--hide' : 'capitalize' ) );

        // Abertura do modal de confirmação da edição do guião.
        const openConfirmationModal = value => { modals.confirmForceEdit = value; };

        // Abertura de um modal.
        const openModal = name => {
            modals[name] = true;
            actions( 'controlActionBar', { value: true });
        };

        // Função que faz fechar um modal e faz reset dos dados antes da edição.
        const closeModal = modal => {
            // Se algum modal, que não seja o de 'pré-visualizar guião', fechar
            // é feito o reset no payload.
            if ( modal !== 'preview' ) {
                actions( 'resetModel' );
                actions( 'controlActionBar', { value: false });
            } else {
                // Caso seja o modal de pré-visualização, retira apenas as keys que
                // sejam ids (que representam as respostas dadas ao interagir com o guião),
                // e as keys com o padrão 'additional-behaviour-answer[id]' correspondentes
                // ao valor da input adicionar de uma resposta de escolha múltipla.
                const payloadKeys = Object.keys( store.payload );
                const scriptIds = state.script.questions.map( question => question.id );
                const payloadKeyIds = payloadKeys.filter( key => !Number.isNaN( transformToInt( key ) )
                    && scriptIds.includes( transformToInt( key ) ) );

                payloadKeyIds.forEach( key => actions( 'deleteField', { name: key }) );

                payloadKeys.forEach( key => {
                    if ( key.includes( 'additional-behaviour-answer[' ) )
                        actions( 'deleteField', { name: key });
                });

                actions( 'controlActionBar', { value: false });
            }

            modals[modal] = false;
        };

        // Função que faz fechar um modal e faz reset dos dados antes da edição.
        const submitModal = ({ additionalParams = {} }) => {
            Object.keys( additionalParams ).forEach( key => {
                actions( 'registerField', { name: key, value: additionalParams[key] });
            });

            setTimeout( () => {
                actions( 'submit', {});
            }, 300 );

            modals.confirmForceEdit = false;
        };

        /**
         * [method]
         * Função que permite ao utilizador pré-visualizar um guião,
         * através de um modal, à medida que vai sendo criado.
         *
         * @since 1.2.3
         * @category method
         * @autor Jore Monteiro
         *
        */
        const openPreviewScript = () => {
            previewScriptHelpers.script = Object.freeze( getScriptToPreview({ script: state.script, payload: store.payload }) );

            // Abre modal do preview do script e
            // Esconde a barra de edição
            modals.preview = !modals.preview;
            actions( 'controlActionBar', { value: true });
        };

        const previewScriptHelpers = reactive({
            visits: [],
            sequence: 1,
            script: {}
        });

        /**
         * [method]
         * Função que permite cria o objecto que irá ser passado para
         * mostrar a pré-visualização de um script
         *
         * @since 1.2.3
         * @category method
         * @autor Jore Monteiro
         *
        */
        const getScriptToPreview = ({ script, payload }) => {
            const questions = [];
            const questionNotDeleted = script.questions.filter( question => question.operation !== 'delete' );

            questionNotDeleted.forEach( question => {
                // Encontrar id do tipo de input da questão.
                const inputType = transformToInt( payload[`type[${question.id}]`] ) || question?.type?.id;

                // Criação das possible answrrs
                const answers = Array.isArray( question.nextQuestions )
                    ? question.nextQuestions
                    : isAnObject( question.nextQuestions )
                        ? [ question.nextQuestions ]
                        : [];

                const finalAnswers = [];
                if ( ![ 3, 4, 5 ].includes( inputType ) ) {
                    // Obtém próxima pergunta através de dados do payload se existir.
                    let payloadNexQuestion = payload[`next-question[${question.id}]`];
                    payloadNexQuestion = ( payloadNexQuestion ) ? script.questions[payloadNexQuestion]?.id : null;

                    // Obtém próxima pergunta através de dados do script se existir.
                    const scriptNexQuestion = question?.nextQuestions?.id || null;

                    finalAnswers.push({
                        id: question?.nextQuestions?.idAnswer || 1,
                        unlockQuestionId: payloadNexQuestion || scriptNexQuestion || null,
                        value: null
                    });
                } else {
                    const answerIds = answers.filter( answer => answer?.operation !== 'delete' ).map( answer => answer?.idAnswer || answer?.id );
                    let answerIdsPayload = Object.keys( payload ).filter( key => key.includes( `question[${question.id}]-answer` )
                                    && !key.includes( 'next-' ) && !key.includes( 'delete-' ) );
                    answerIdsPayload = answerIdsPayload.map( key => transformToInt( key.match( /(?<=\[).*?(?=\])/g )[1] ) );
                    let uniqueAnswerIds = [ ...answerIds, ...answerIdsPayload ];
                    uniqueAnswerIds = [ ...new Set( uniqueAnswerIds ) ];

                    uniqueAnswerIds.forEach( id => {
                        let nextQuestionId = null;
                        const payloadNexQuestion = payload[`next-question[${question.id}]`] || payload[`next-question[${question.id}]-answer[${id}]`];
                        if ( payloadNexQuestion )
                            nextQuestionId = script.questions[payloadNexQuestion]?.id;
                        else {
                            const ansr = answers.find( answer => answer.idAnswer === id || answer.id === id );
                            nextQuestionId = ansr?.nextQuestion?.id || null;
                        }

                        finalAnswers.push({
                            id,
                            unlockQuestionId: nextQuestionId,
                            value: payload[`question[${question.id}]-answer[${id}]`] || answers.find( answer => answer.id === id )?.value || null,
                            additionalBehaviour: payload[`additional-behaviour-question[${question.id}]-answer[${id}]`]
                                || answers.find( answer => answer.id === id )?.additionalBehaviour || false
                        });
                    });
                }

                questions.push({
                    id: question.id,
                    question: payload[`question[${question.id}]`] || question.question,
                    inputType: ( inputType !== 100 )
                        ? { id: inputType, name: scriptInputTypes.find( type => type.value === inputType )?.label }
                        : null,
                    sequence: 1,
                    possibleAnswers: finalAnswers
                });
            });

            // filtrar perguntas que não tenham possibleAnswers ou que tenham possibleAnswers mas que nenhuma tenha unlockQuestionId
            const firstQuestionWithoutChild = questions.filter( qstn => qstn.possibleAnswers?.length === 0
            || qstn.possibleAnswers.some( answer => !answer.unlockQuestionId ) );

            const firstQuestion = scriptFirstQuestion( firstQuestionWithoutChild[0], questions );

            previewScriptHelpers.visits = [];
            previewScriptHelpers.sequence = 1;
            recalculateSequence( firstQuestion, questions );

            return {
                name: payload.scriptName || props?.model?.name,
                type: { id: getScriptType.value, name: campaignScriptTypes[getScriptType.value] },
                questions
            };
        };

        /**
         * [method]
         * Função que permite calcular a 1ª pergunta do guião.
         *
         * @since 1.2.3
         * @category method
         * @autor Jore Monteiro
         *
        */
        const scriptFirstQuestion = ( firstQuestionWithoutChild, questions ) => {
            /* eslint-disable */
            for ( const question of questions ) {
                for ( const answer of question.possibleAnswers ) {
                    if ( answer.unlockQuestionId === firstQuestionWithoutChild.id )
                        return scriptFirstQuestion( question, questions );
                }
            }
            /* eslint-enable */

            return firstQuestionWithoutChild;
        };

        const recalculateSequence = ( point, questions ) => {
            /* eslint-disable */
            for ( const answer of point.possibleAnswers ) {
                let duplicate = false;
                const nextQuestion = questions.find( question => question.id === answer.unlockQuestionId );

                for ( const possibleDuplicate of previewScriptHelpers.visits ) {
                    if ( nextQuestion && possibleDuplicate?.point?.id === nextQuestion.id ) {
                        duplicate = true;
                        break;
                    }
                }

                if ( nextQuestion ) {
                    previewScriptHelpers.sequence++;
                    nextQuestion.sequence = previewScriptHelpers.sequence;

                    if ( duplicate ) {
                        const nextQuestionInVisits = previewScriptHelpers.visits.find( visit => visit.point?.id === nextQuestion.id );

                        if ( nextQuestionInVisits ) {
                            nextQuestionInVisits.point = nextQuestion;

                            for ( const reviewVisit of previewScriptHelpers.visits ) {
                                if (nextQuestion && reviewVisit?.point?.id === nextQuestion.id)
                                    recalculateSequence( reviewVisit.point, questions );
                            }

                        }
                    } else {
                        previewScriptHelpers.visits.push({ point: nextQuestion, visited: duplicate });
                    }
                }
            }

            for ( const visit of previewScriptHelpers.visits ) {
                if ( visit.visited === false ) {
                    visit.visited = true;
                    recalculateSequence( visit.point, questions );
                }
            }

            /* eslint-enable */
        };

        watch( () => modals.duplicateScript,
            modalDuplicateScript => {
                if ( modalDuplicateScript && !requestData.campaigns.drop.length )
                    getCampaignsRequest();
            });

        const getCampaignsRequest = ( async () => {
            requestData.campaigns.loading = true;
            requestData.campaigns.drop = await get( '/campaigns' )
                .then( ({ data }) => data.content.data );
            requestData.campaigns.drop = requestData.campaigns.drop.map( key => ({ value: key.id, label: `${key.id} - ${key.name}` }) );
            // ordenar drop dos das campanhas
            sortObjectsArray( requestData.campaigns.drop );
            requestData.campaigns.loading = false;
        });

        // Se o tipo de guião for alterado para 'sequencial',
        // Obtem todos os campos das próximas perguntas e
        // elimina-os do payload se já existir valor.
        watch( () => store.payload.typeId,
            newScriptType => {
                if ( newScriptType === '2' ) {
                    const nextQuestions = Object.keys( store.payload ).filter( key => key.includes( 'next-question[' ) );

                    nextQuestions.forEach( key => {
                        actions( 'deleteField', { name: key, payloadKey: 'payload' });
                    });
                }
            });

        return {
            ...toRefs( state ),
            ...toRefs( store ),
            ...toRefs( requestData ),
            modals,
            previewScriptHelpers,
            controllerQuestion,
            getNewQuestionId,
            errors,
            reset,
            zIndexes,
            generateLabelClass,
            openModal,
            openConfirmationModal,
            closeModal,
            submitModal,
            capitalizeFirstLetter,
            getScriptType,
            openPreviewScript,
            getScriptToPreview
        };
    }
};
</script>

<style lang="scss">
    .script {
        max-width: 800px;
        margin: 0 auto;
        &__header {
            grid-template-columns: 2fr 1.2fr 1fr;
        }
        &__label {
            font-size: 20px;
        }
        &__type {
            font-size: 18px;
            font-weight: 400;
        }
        &__questions {
            &__content {
                margin-bottom: 30px;
                &--animation {
                    &-enter {
                        opacity: 0;
                        @include transform( translateY( -30px ) );
                        will-change: opacity, transform;
                    }
                }
                &__group {
                    @include transition( all .3s ease-in-out );
                    will-change: opacity, transform;
                }
            }
            &__question {
                grid-template-columns: 2fr 1fr;
                grid-gap: 60px;
                row-gap: 30px;
                &__actions {
                    @include transform-position( 90px, 30px, false, false );
                }
            }
            &__add {
                &__button {
                    @include transform-position( false, 0, -65px, false );
                }
            }
        }
    }
</style>